// The Sources list's naming and sorting, apart from the page so tests can load them without MSAL.

import { APPLICABILITY_LABELS, type SharePointSource } from "@ask-hr/core/sources";
import { isArchived } from "@ask-hr/core/documents";
import { VISIBILITY_ORDER, isHidden, sourceState } from "@ask-hr/core/visibility";
import type { Doc } from "./documents.js";
import { sortRows, type Sort, type SortValues } from "./sort.js";

export type SourceSortKey = "source" | "folder" | "applies" | "employees" | "added";
export const SOURCE_DESC_FIRST: readonly SourceSortKey[] = ["added"];

const SORT_VALUE: SortValues<SharePointSource, SourceSortKey> = {
  source: (s) => label(s),
  // Whole libraries ("/") sort ahead of their folders.
  folder: (s) => (s.kind === "web" ? hostPath(s.webUrl) : s.path),
  applies: (s) => APPLICABILITY_LABELS[s.applicability] ?? s.applicability,
  // Fewest people first; websites and sources not checked yet go last.
  employees: (s) => {
    const state = sourceState(s.visibility);
    return state ? VISIBILITY_ORDER[state] : 9;
  },
  added: (s) => s.addedAt,
};

export function sortSources(sources: SharePointSource[], sort: Sort<SourceSortKey>): SharePointSource[] {
  return sortRows(sources, sort, SORT_VALUE, label);
}

export function hostPath(url: string): string {
  try {
    const u = new URL(url);
    return `${u.hostname}${u.pathname === "/" ? "" : u.pathname}`;
  } catch {
    return url;
  }
}

export function label(s: { driveName: string; path: string }): string {
  return s.path === "/" ? s.driveName : `${s.driveName}${s.path}`;
}

// ---- each source's documents, for the Sources list and its panel

export interface SourceCounts {
  total: number; // in the list (not removed; archived ones only when approved)
  review: number; // new or changed
  hidden: number; // most employees can't open
  own: number; // with their own Applies to
}

export function sourceCounts(docs: readonly Doc[]): Map<string, SourceCounts> {
  const out = new Map<string, SourceCounts>();
  for (const d of docs) {
    if (d.status === "removed" || (isArchived(d) && d.status !== "approved")) continue;
    const c = out.get(d.sourceId) ?? { total: 0, review: 0, hidden: 0, own: 0 };
    c.total += 1;
    if (d.status === "new" || d.status === "changed") c.review += 1;
    if (isHidden(d.visibility)) c.hidden += 1;
    if (d.applicability) c.own += 1;
    out.set(d.sourceId, c);
  }
  return out;
}

// Documents, filtered to one source (and optionally more): what the Sources links open.
export function documentsLink(sourceId: string, more: { tab?: "review" | "all"; permissions?: "hidden" } = {}): string {
  const q = new URLSearchParams({ source: sourceId, tab: more.tab ?? "all", ...(more.permissions ? { permissions: more.permissions } : {}) });
  return `/admin/documents?${q.toString()}`;
}
